import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import IncidentLogCard from './components/IncidentLogCard';
import AnswerControls from './components/AnswerControls';
import { FAILURE_CLASSIFICATIONS, SEVERITY_OPTIONS, NEXT_ACTIONS } from '@/mocks/challenges';
import {
  loadTodayChallenge,
  submitChallengeResult,
  hasSubmittedResult,
  markSubmittedResult,
} from '@/lib/challenge';
import type { Challenge } from '@/types/challenge';
import { getBestStreak, getCurrentStreak, recordChallengePlayed } from '@/lib/streak';

export default function ChallengePage() {
  const navigate = useNavigate();
  const [challenge, setChallenge] = useState<Challenge | null>(null);
  const [classification, setClassification] = useState('');
  const [severity, setSeverity] = useState('');
  const [nextAction, setNextAction] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTodayChallenge()
      .then((data) => {
        setChallenge(data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Challenge load failed:', error);
        setLoading(false);
      });
  }, []);

  const stats = useMemo(
    () => ({
      streak: getCurrentStreak(),
      best: getBestStreak(),
      rank: '#8',
    }),
    []
  );

  const handleSubmit = () => {
    if (!challenge || !classification || !severity || !nextAction) return;

    const correct = challenge.correctAnswer;
    let score = 0;

    if (classification === correct.classification) score += 10;
    if (severity === correct.severity) score += 5;
    if (nextAction === correct.nextAction) score += 10;

    recordChallengePlayed();

    const solvedCorrectly = score === 25;

    if (!hasSubmittedResult(challenge.date)) {
      const challengeStats = submitChallengeResult(challenge.date, solvedCorrectly);
      localStorage.setItem('explainerror_stats', JSON.stringify(challengeStats));
      markSubmittedResult(challenge.date);
    }

    const result = {
      challengeId: challenge.id,
      challengeDate: challenge.date,
      classification,
      severity,
      nextAction,
      score,
    };

    localStorage.setItem('explainerror_result', JSON.stringify(result));
    const existingHistory = JSON.parse(localStorage.getItem('explainerror_history') || '[]');

    const alreadyExists = existingHistory.some(
      (item: { challengeDate: string }) => item.challengeDate === result.challengeDate
    );

    const updatedHistory = alreadyExists
      ? existingHistory.map((item: { challengeDate: string }) =>
          item.challengeDate === result.challengeDate ? result : item
        )
      : [...existingHistory, result];

    localStorage.setItem('explainerror_history', JSON.stringify(updatedHistory));
    navigate('/result');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-base font-sans">
        <Navbar />
        <main className="pt-24 px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center text-text-secondary">
            Loading today&apos;s challenge...
          </div>
        </main>
      </div>
    );
  }

  if (!challenge) {
    return (
      <div className="min-h-screen bg-bg-base font-sans">
        <Navbar />
        <main className="pt-24 px-4 md:px-6">
          <div className="max-w-3xl mx-auto text-center text-text-secondary">
            Could not load today&apos;s challenge.
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-base font-sans">
      <Navbar />
      <main className="pt-24 pb-16 px-4 md:px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 animate-slide-up">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-bg-panel border border-accent/30 text-accent text-xs font-mono uppercase tracking-widest mb-4">
              <span className="w-1.5 h-1.5 rounded-full bg-accent animate-pulse" />
              Daily Challenge — {challenge.date}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-text-primary mb-3">
              Daily Challenge
            </h1>
            <p className="text-text-secondary text-sm">
              One production incident. One minute. What is your call?
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_0.9fr] gap-6 animate-fade-in">
            <div className="space-y-6">
              <IncidentLogCard title={challenge.title} logLines={challenge.logLines} />
              <AnswerControls
                classification={classification}
                severity={severity}
                nextAction={nextAction}
                onClassificationChange={setClassification}
                onSeverityChange={setSeverity}
                onNextActionChange={setNextAction}
                onSubmit={handleSubmit}
                classifications={FAILURE_CLASSIFICATIONS}
                severities={SEVERITY_OPTIONS}
                actions={NEXT_ACTIONS}
              />
            </div>

            <aside className="bg-bg-panel border border-border-subtle rounded-xl p-5 h-fit">
              <h3 className="text-sm font-semibold text-text-primary uppercase tracking-wider mb-4">
                Your stats
              </h3>
              <div className="space-y-4 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-text-muted">Current streak</span>
                  <span className="font-mono text-accent">{stats.streak}d</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-text-muted">Best streak</span>
                  <span className="font-mono text-text-primary">{stats.best}d</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-text-muted">Weekly rank</span>
                  <span className="font-mono text-text-primary">{stats.rank}</span>
                </div>
              </div>
              <div className="mt-6 pt-6 border-t border-border-subtle">
                <p className="text-xs text-text-muted leading-relaxed">
                  Read the signal, not the noise. Focus on the most likely failure pattern and your
                  first operational move.
                </p>
              </div>
            </aside>
          </div>
        </div>
      </main>
    </div>
  );
}